import React, { useMemo } from 'react';
import RestfulWrapper from "../../../components/RestfulWrapper/RestfulWrapper";
import { useIoContext, useStore, useStoreActions } from "../../../contexts";
import GraphWidgetLayout from "../GraphWidget/GraphWidgetLayout";
import GraphWidgetTopCard from "../GraphWidget/GraphWidgetTopCard";
import GraphWidgetBottomCard from "../GraphWidget/GraphWidgetBottomCard";
import { Divider, Spinner, TableList, TextButton, Typography, WithInfoIcon } from 'seagull';
import { routes } from "../../../constants";
import styles from "./PayoutsSummaryWidget.module.scss";
import { selectors } from "../../../store/state-selectors";
import GenericError from "../../../components/GenericError/GenericError";
import { MoneyModel, PayoutSummaryStatus, PayoutsSummaryModel, PayoutsSummaryPayoutModel } from "../../../models";
import { isAfter, startOfDay, startOfMonth, subDays } from 'date-fns';
import Decimal from 'decimal.js';
import { NavLink } from 'react-router-dom';
import { InvoiceDisplayStatus } from 'anchor-scout/types';
import compact from 'lodash/compact';
const renderDivider = () => /*#__PURE__*/React.createElement(Divider, {
  margin: 0
});
const renderCell = (value, testid) => ({
  value: /*#__PURE__*/React.createElement(Typography, {
    "data-testid": testid
  }, value)
});
const renderHeaderItem = value => ({
  value: /*#__PURE__*/React.createElement(Typography, {
    weight: "normal"
  }, value)
});
function getTableData() {
  const config = [{
    columnDef: {
      width: [100, 200]
    },
    headerItem: renderHeaderItem('Date')
  }, {
    columnDef: {
      width: [100, 200],
      hAlign: 'end'
    },
    headerItem: renderHeaderItem('Amount')
  }, {
    columnDef: {
      width: 80,
      hAlign: 'end'
    },
    headerItem: renderHeaderItem('Status')
  }];
  return {
    columnDefs: config.map(i => i.columnDef),
    headerItems: config.map(i => i.headerItem)
  };
}
const PayoutsSummaryTable = _ref => {
  let {
    data,
    testid
  } = _ref;
  const {
    columnDefs,
    headerItems
  } = useMemo(() => getTableData(), []);
  return /*#__PURE__*/React.createElement("div", {
    className: styles.table,
    "data-testid": testid
  }, /*#__PURE__*/React.createElement(TableList.Provider, {
    variant: "minimal",
    columnDefs: columnDefs,
    hPadding: "xs"
  }, /*#__PURE__*/React.createElement(TableList.Header, {
    items: headerItems,
    bgColor: "transparent",
    size: "small"
  }), renderDivider(), data.mapPayouts((payout, idx, isLast) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: payout.uniqueKey()
  }, /*#__PURE__*/React.createElement(TableList.Row, {
    disableMarginBottom: true,
    highlightOnHover: true,
    size: "medium",
    as: NavLink,
    to: payout.redirectURL(),
    items: compact([renderCell(payout.formattedDate(), "payout-summary-date-".concat(idx)), renderCell(payout.formattedAmount(), "payout-summary-amount-".concat(idx)), renderCell(payout.statusText(), "payout-summary-status-".concat(idx))])
  }), !isLast && renderDivider())), /*#__PURE__*/React.createElement("div", {
    className: styles.viewMore
  }, /*#__PURE__*/React.createElement(TextButton, {
    as: NavLink,
    to: routes.payoutsDashboard,
    "data-testid": "payouts-summary-view-more",
    underline: "none",
    weight: "normal"
  }, "View more"), /*#__PURE__*/React.createElement(Typography, {
    variant: "paragraph3",
    skin: "secondary"
  }, "* Future payouts may vary in amount and date"))));
};
export function getEmptyData(now) {
  const firstOfMonth = startOfMonth(now);
  let dateToAdd = startOfDay(now);
  const payouts = [];
  while (!isAfter(firstOfMonth, dateToAdd) && payouts.length < 5) {
    payouts.push(new PayoutsSummaryPayoutModel({
      id: '',
      date: dateToAdd,
      amount: new MoneyModel(new Decimal(1200)),
      status: PayoutSummaryStatus.Paid
    }));
    dateToAdd = subDays(dateToAdd, 1);
  }
  return PayoutsSummaryModel.makeEmpty().addPayouts(payouts);
}
function PlaceholderTable() {
  const {
    getNow
  } = useIoContext();
  const emptyData = useMemo(() => getEmptyData(getNow()), []);
  return /*#__PURE__*/React.createElement(PayoutsSummaryTable, {
    testid: "payouts-summary-table-placeholder",
    data: emptyData
  });
}
const TopCard = _ref2 => {
  let {
    testid,
    loading,
    label,
    info,
    value,
    linkTo
  } = _ref2;
  return /*#__PURE__*/React.createElement(GraphWidgetTopCard, {
    linkTo: linkTo,
    "data-testid": loading ? "".concat(testid, "-loading") : testid
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.topCard
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(WithInfoIcon, {
    info: info
  }, /*#__PURE__*/React.createElement(Typography, null, label))), /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    weight: "normal",
    "data-testid": "".concat(testid, "-value")
  }, loading ? /*#__PURE__*/React.createElement(Spinner, {
    size: 30,
    "data-testid": "".concat(testid, "-loading-spinner")
  }) : value)));
};
function getFuturePayoutsLink() {
  const filters = {
    status: [InvoiceDisplayStatus.PaymentInitiated]
  };
  const params = new URLSearchParams();
  Object.keys(filters).forEach(key => {
    params.set(key, filters[key].join(','));
  });
  return "".concat(routes.invoicesDashboard, "?").concat(params);
}
function PayoutsSummaryWidgetContent(_ref3) {
  let {
    loading,
    data
  } = _ref3;
  const noData = !data;
  const topCards = [/*#__PURE__*/React.createElement(TopCard, {
    key: "paid",
    testid: "payouts-summary-paid-this-month",
    loading: noData,
    label: "Paid this month",
    info: "Funds transferred to your bank account this month via Anchor.",
    value: data ? data.formattedPaidThisMonth() : '',
    linkTo: routes.payoutsDashboard
  }), /*#__PURE__*/React.createElement(TopCard, {
    key: "future",
    testid: "payouts-summary-future-payouts",
    loading: noData,
    label: "Processing",
    info: "Funds still in transit from your clients' accounts. This process can take up to 3 business days and may be affected by refunds or payment failures.",
    value: data ? data.formattedFuturePayouts() : '',
    linkTo: getFuturePayoutsLink()
  })];
  return /*#__PURE__*/React.createElement(GraphWidgetLayout, {
    title: "Payouts",
    loading: loading,
    bottomHeight: "l"
  }, topCards, /*#__PURE__*/React.createElement(GraphWidgetBottomCard, {
    "data-testid": "payouts-summary",
    loading: noData,
    loadingMsg: "Loading data...",
    empty: data && data.isEmpty(),
    emptyMsg: "Here you will view your payouts",
    loadingOrEmptyBg: () => /*#__PURE__*/React.createElement(PlaceholderTable, null)
  }, data && /*#__PURE__*/React.createElement(PayoutsSummaryTable, {
    testid: "payouts-summary-table",
    data: data
  })));
}
export default function PayoutsSummaryWidget() {
  const {
    fetchPayoutsSummary
  } = useStoreActions();
  const initialData = useStore(state => state.payoutsSummary.data);
  const isMemberUser = useStore(selectors.user.isMemberUser);
  return isMemberUser ? null : /*#__PURE__*/React.createElement(RestfulWrapper, {
    initialData: initialData,
    fetch: fetchPayoutsSummary,
    renderChild: _ref4 => {
      let {
        data,
        loading
      } = _ref4;
      return /*#__PURE__*/React.createElement(PayoutsSummaryWidgetContent, {
        data: data,
        loading: loading
      });
    },
    renderLoader: () => /*#__PURE__*/React.createElement(PayoutsSummaryWidgetContent, {
      loading: true
    }),
    renderError: () => /*#__PURE__*/React.createElement(GenericError, {
      testid: "widget-error"
    })
  });
}